import { connect } from "react-redux"

// component functions
import * as OwnFunctions from './ContractAccess.Form.Functions'
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, MessageBar, MessageBarBody, MessageBarTitle, OverlayDrawer, Spinner } from "@fluentui/react-components"
import { Dismiss24Regular } from "@fluentui/react-icons"
import { MessageComponent } from "../../../../commons/component/MessageComponent"
import { MessageBarType, Separator, Toggle } from "@fluentui/react"
import { useEffect, useState } from "react"
import { NewContractAccess } from "../../../../models/Entities/Contracts/NewContractAccess"
import { ContractAccess } from "../../../../models/Entities/Contracts/ContractAccess"
import UserSelectorComponent from "../../../../commons/component/Inputs/UserSelector/UserSelector.Component"
import { User } from "../../../../models/Entities/Authentication/User"

// styles
import './ContractAccess.Form.Styles.css'

interface FormValidation {
    success: boolean;
    errors: string[];
}

const ContractAccessFormComponent = (props: OwnFunctions.Props) => {

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();
    const [userAlreadySelected, setUserAlreadySelected] = useState<boolean>(false);

    // form data
    const [UserId, setUserId] = useState<string | undefined>();
    const [ReadAccess, setReadAccess] = useState<boolean>(false);
    const [WriteAccess, setWriteAccess] = useState<boolean>(false);

    // initialize form from CurrentAccess when editing
    useEffect(() => {
        if (props.CurrentAccess) {
            setUserId(props.CurrentAccess.userID);
            setReadAccess(props.CurrentAccess.readAccess);
            setWriteAccess(props.CurrentAccess.writeAccess);
        } else {
            setUserId(undefined);
            setReadAccess(false);
            setWriteAccess(false);
        }
    }, [props.CurrentAccess]);

    // write access implies read access
    const handleWriteAccessChange = (checked: boolean) => {
        setWriteAccess(checked);
        if (checked) setReadAccess(true);
    };

    const handleReadAccessChange = (checked: boolean) => {
        setReadAccess(checked);
        if (!checked) setWriteAccess(false);
    };

    const onSelectUser = (users: User[]) => {
        if (users.length > 0) {
            const user = users[0];
            const alreadySelected = props.SelectedUsersIds?.find(a => a === user.userId);
            setUserAlreadySelected(!!alreadySelected);
        } else {
            setUserAlreadySelected(false);
        }
        setUserId(users[0]?.userId);
    }

    useEffect(() => {
        if (props.error) {
            setSubmittedForm(false);
        }
    }, [props.error]);

    useEffect(() => {
        if (props.isSavingSuccessfully) {
            ResetForm();
            props.dismissPanel();
        }
    }, [props.isSavingSuccessfully]);

    // form validation
    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;
        let FormValidation: FormValidation = { success: true, errors: [] };

        if (!UserId) {
            FormValidation.errors.push("Debe seleccionar un usuario");
            validationResult = false;
        }

        if (!ReadAccess && !WriteAccess) {
            FormValidation.errors.push("Debe otorgar al menos un permiso (lectura o escritura)");
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    // reset form
    const ResetForm = () => {
        setSubmittedForm(false);
        setUserId(undefined);
        setReadAccess(false);
        setWriteAccess(false);
        setUserAlreadySelected(false);
        setFormValidationStatus(undefined);
    };

    // events
    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    const HandleSubmitForm = () => {
        setSubmittedForm(true);

        if (ValidateForm()) {
            if (!props.CurrentAccess) {
                const newAccess: NewContractAccess = {
                    contractID: props.ContractId,
                    userID: UserId || '',
                    readAccess: ReadAccess,
                    writeAccess: WriteAccess
                };
                props.CreateContractAccess(newAccess);
            } else {
                const access: ContractAccess = {
                    id: props.CurrentAccess.id,
                    contractID: props.ContractId,
                    userID: props.CurrentAccess.userID,
                    userName: props.CurrentAccess.userName,
                    readAccess: ReadAccess,
                    writeAccess: WriteAccess
                };
                props.UpdateContractAccess(access);
            }
        } else {
            setSubmittedForm(false);
        }
    };

    const isEditMode = !!props.CurrentAccess;

    const Footer = () => (
        <DrawerFooter>
            <Button onClick={HandleCancelForm} disabled={submittedForm}>Cancelar</Button>
            <Button
                appearance="primary"
                onClick={HandleSubmitForm}
                disabled={submittedForm || (!isEditMode && userAlreadySelected)}
            >
                {props.isSaving && <><Spinner size="extra-tiny" />&nbsp;</>}
                {isEditMode ? 'Actualizar' : 'Guardar'}
            </Button>
        </DrawerFooter>
    );

    return (
        <OverlayDrawer position={'end'} open={props.isOpen}>
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<Dismiss24Regular />}
                            onClick={HandleCancelForm}
                        />
                    }
                >
                    {isEditMode ? 'Editar permisos de acceso' : 'Agregar acceso al contrato'}
                </DrawerHeaderTitle>
                {FormValidationStatus?.success === false && (
                    <MessageComponent
                        message={"Verifique la información suministrada"}
                        type={MessageBarType.error}
                        itemList={FormValidationStatus?.errors}
                    />
                )}
                {props.failOnSaving === true && (
                    <MessageComponent
                        message={props.error?.ErrorMessage || 'Ocurrió un error inesperado'}
                        type={MessageBarType.error}
                        itemList={props.error?.Errors}
                    />
                )}
            </DrawerHeader>

            <DrawerBody className='ScrollShadow'>
                {!isEditMode && (
                    <>
                        <UserSelectorComponent
                            onChangeSelection={onSelectUser}
                            UserSelected={undefined}
                            SelectedUsersIds={[]}
                            fieldName={"Seleccione un usuario"}
                            selectionLimit={1}
                        />
                        {userAlreadySelected && (
                            <MessageBar intent={"warning"}>
                                <MessageBarBody>
                                    <MessageBarTitle>Atención</MessageBarTitle>
                                    El usuario seleccionado ya tiene accesos al contrato.
                                </MessageBarBody>
                            </MessageBar>
                        )}
                    </>
                )}

                {isEditMode && (
                    <div className="accessFormUserInfo">
                        <strong>Usuario:</strong> {props.CurrentAccess?.userName || props.CurrentAccess?.userID}
                    </div>
                )}

                <Separator />

                <div className="accessPermissionsSection">
                    <p className="accessPermissionsLabel">Permisos</p>

                    <div className="accessPermissionRow">
                        <Toggle
                            label="Puede ver el contrato (lectura)"
                            checked={ReadAccess}
                            onText="Sí"
                            offText="No"
                            disabled={WriteAccess}
                            onChange={(ev, checked) => handleReadAccessChange(checked || false)}
                        />
                    </div>

                    <div className="accessPermissionRow">
                        <Toggle
                            label="Puede editar el contrato (escritura)"
                            checked={WriteAccess}
                            onText="Sí"
                            offText="No"
                            onChange={(ev, checked) => handleWriteAccessChange(checked || false)}
                        />
                    </div>

                    {WriteAccess && (
                        <MessageBar intent="info">
                            <MessageBarBody>
                                El permiso de escritura incluye automáticamente el permiso de lectura.
                            </MessageBarBody>
                        </MessageBar>
                    )}
                </div>
            </DrawerBody>
            <Footer />
        </OverlayDrawer>
    );
}

export default connect(
    OwnFunctions.mapStateToProps,
    OwnFunctions.mapDispatchToProps
)(ContractAccessFormComponent as any)